import { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, message, Modal } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  ApartmentOutlined,
  SafetyCertificateOutlined,
  CloudServerOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import { authApi } from '../../services/authService';
import { useNavigate } from 'react-router-dom';
import './Login.css';

function LoginFlowCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const pointerRef = useRef({ x: -9999, y: -9999, tx: -9999, ty: -9999 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    let width = 0;
    let height = 0;
    let dpr = 1;
    let time = 0;
    const particles = Array.from({ length: 108 }, (_, i) => ({
      base: Math.random(),
      lane: i % 7,
      speed: 0.0012 + Math.random() * 0.0018,
      size: 1 + Math.random() * 2.2,
      alpha: 0.18 + Math.random() * 0.24,
    }));

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.offsetWidth || window.innerWidth;
      height = canvas.offsetHeight || window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const drawRibbon = (offset: number, color: string, alpha: number, lift: number) => {
      const beam = new Path2D();
      const startY = height * (0.62 + lift);
      const breath = 0.72 + Math.sin(time * 0.028 + offset) * 0.16;
      const softColor = color === '37,99,235' ? '125,211,252' : color === '124,58,237' ? '196,181,253' : '103,232,249';
      beam.moveTo(width * -0.08, startY);
      for (let x = -80; x <= width + 120; x += 44) {
        const wave = Math.sin(x * 0.006 + time * 0.012 + offset) * 34;
        const drift = Math.cos(x * 0.0028 + time * 0.008 + offset) * 18;
        const y = startY + wave + drift - x * 0.13;
        beam.lineTo(x, y);
      }

      const gradient = ctx.createLinearGradient(width * 0.05, height * 0.5, width * 0.92, height * 0.22);
      gradient.addColorStop(0, `rgba(${softColor},0)`);
      gradient.addColorStop(0.22, `rgba(${softColor},${alpha * 0.22 * breath})`);
      gradient.addColorStop(0.48, `rgba(${softColor},${alpha * 0.62 * breath})`);
      gradient.addColorStop(0.70, `rgba(${color},${alpha * 0.36 * breath})`);
      gradient.addColorStop(1, `rgba(${softColor},0)`);

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.strokeStyle = gradient;
      ctx.lineWidth = 13;
      ctx.shadowColor = `rgba(${softColor},${alpha * 0.92 * breath})`;
      ctx.shadowBlur = 52;
      ctx.stroke(beam);

      ctx.lineWidth = 6.2;
      ctx.shadowColor = `rgba(${softColor},${alpha * 0.72 * breath})`;
      ctx.shadowBlur = 34;
      ctx.stroke(beam);

      ctx.strokeStyle = `rgba(255,255,255,${Math.min(0.28, alpha * 0.82 * breath)})`;
      ctx.lineWidth = 1.05;
      ctx.shadowColor = `rgba(255,255,255,${alpha * 0.46 * breath})`;
      ctx.shadowBlur = 16;
      ctx.stroke(beam);

      const pulse = ctx.createLinearGradient(width * 0.08, startY + 20, width * 0.72, startY - 120);
      pulse.addColorStop(0, 'rgba(255,255,255,0)');
      pulse.addColorStop(0.45, `rgba(255,255,255,${0.06 + breath * 0.08})`);
      pulse.addColorStop(0.58, `rgba(${softColor},${alpha * 0.26 * breath})`);
      pulse.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.strokeStyle = pulse;
      ctx.lineWidth = 2.2;
      ctx.shadowColor = `rgba(${softColor},${alpha * 0.42 * breath})`;
      ctx.shadowBlur = 20;
      ctx.stroke(beam);

      ctx.shadowBlur = 0;
    };

    const drawBrandConstellation = () => {
      const p = pointerRef.current;
      const cx = Math.min(width * 0.34, 520);
      const cy = height * 0.54;
      const nodes = 18;
      for (let i = 0; i < nodes; i++) {
        const angle = (Math.PI * 2 * i) / nodes + time * 0.006;
        const rx = 175 + Math.sin(time * 0.01 + i) * 24;
        const ry = 88 + Math.cos(time * 0.008 + i * 0.7) * 16;
        const x = cx + Math.cos(angle) * rx;
        const y = cy + Math.sin(angle) * ry;
        const nextAngle = (Math.PI * 2 * (i + 1)) / nodes + time * 0.006;
        const nx = cx + Math.cos(nextAngle) * rx;
        const ny = cy + Math.sin(nextAngle) * ry;

        ctx.beginPath();
        ctx.strokeStyle = 'rgba(37, 99, 235, .22)';
        ctx.lineWidth = 1;
        ctx.moveTo(x, y);
        ctx.lineTo(nx, ny);
        ctx.stroke();

        const dist = p.tx > -1000 ? Math.hypot(p.x - x, p.y - y) : 9999;
        const pull = dist < 260 ? 1 - dist / 260 : 0;
        if (pull > 0) {
          ctx.beginPath();
          ctx.strokeStyle = `rgba(124, 58, 237, ${0.12 + pull * 0.32})`;
          ctx.lineWidth = 1 + pull * 1.2;
          ctx.moveTo(x, y);
          ctx.quadraticCurveTo((x + p.x) / 2, y - 34 * pull, p.x, p.y);
          ctx.stroke();
        }

        ctx.beginPath();
        const hue = i % 3 === 0 ? '37,99,235' : i % 3 === 1 ? '124,58,237' : '6,182,212';
        ctx.fillStyle = `rgba(${hue}, ${0.36 + pull * 0.46})`;
        ctx.shadowColor = `rgba(${hue}, ${0.28 + pull * 0.48})`;
        ctx.shadowBlur = 12 + pull * 28;
        ctx.arc(x, y, 2.2 + pull * 2.6, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    };

    const draw = () => {
      time += reduced ? 0 : 1;
      const p = pointerRef.current;
      p.x += (p.tx - p.x) * 0.075;
      p.y += (p.ty - p.y) * 0.075;

      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'source-over';

      drawRibbon(0.2, '37,99,235', 0.30, -0.04);
      drawRibbon(1.8, '124,58,237', 0.24, 0.02);
      drawRibbon(3.1, '6,182,212', 0.22, 0.08);
      drawBrandConstellation();

      particles.forEach((dot, index) => {
        const progress = (dot.base + time * dot.speed) % 1;
        const x = width * (0.05 + progress * 0.86);
        const centerY = height * (0.70 - progress * 0.38);
        const y = centerY + Math.sin(progress * Math.PI * 4 + dot.lane) * 44 + dot.lane * 8;
        const dx = p.x - x;
        const dy = p.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const pull = dist < 190 ? (1 - dist / 190) : 0;
        const px = x + (dx / Math.max(dist, 1)) * pull * 28;
        const py = y + (dy / Math.max(dist, 1)) * pull * 28;
        const hue = index % 3 === 0 ? '37,99,235' : index % 3 === 1 ? '124,58,237' : '6,182,212';
        ctx.beginPath();
        ctx.fillStyle = `rgba(${hue},${dot.alpha + 0.12 + pull * 0.52})`;
        ctx.shadowColor = `rgba(${hue},${0.30 + pull * 0.46})`;
        ctx.shadowBlur = 16 + pull * 34;
        ctx.arc(px, py, dot.size + pull * 2.4, 0, Math.PI * 2);
        ctx.fill();
      });

      if (p.tx > -1000) {
        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 260);
        glow.addColorStop(0, 'rgba(124, 58, 237, .18)');
        glow.addColorStop(0.38, 'rgba(37, 99, 235, .12)');
        glow.addColorStop(0.68, 'rgba(6, 182, 212, .07)');
        glow.addColorStop(1, 'rgba(37, 99, 235, 0)');
        ctx.fillStyle = glow;
        ctx.fillRect(0, 0, width, height);
      }

      ctx.globalCompositeOperation = 'source-over';
      if (!reduced) raf = requestAnimationFrame(draw);
    };

    const onPointerMove = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointerRef.current.tx = event.clientX - rect.left;
      pointerRef.current.ty = event.clientY - rect.top;
      if (pointerRef.current.x < -1000) {
        pointerRef.current.x = pointerRef.current.tx;
        pointerRef.current.y = pointerRef.current.ty;
      }
    };
    const onPointerLeave = () => {
      pointerRef.current = { x: -9999, y: -9999, tx: -9999, ty: -9999 };
    };

    resize();
    draw();
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerleave', onPointerLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerleave', onPointerLeave);
    };
  }, []);

  return <canvas ref={canvasRef} className="login-flow-canvas" aria-hidden="true" />;
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const [regOpen, setRegOpen] = useState(false);

  const handleSubmit = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      const { user, token } = await authApi.login(values);
      login(user, token);
      messageApi.success('登录成功，欢迎回来！');
      navigate('/');
    } catch (error: any) {
      const msg = error.response?.data?.message || error.response?.data?.error || error.message || '登录失败，请检查网络';
      messageApi.error(msg);
      console.error('登录失败:', error.response?.status, msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="login-page">
      {contextHolder}
      <div className="login-bg-photo" />
      <div className="login-bg-wash" />
      <LoginFlowCanvas />

      <section className="login-brand-panel">
        <div className="login-kicker"><ApartmentOutlined /> DevForge AI Agent</div>
        <div className="login-brand-row">
          <img className="login-official-logo" src="/assets/devforge-logo.png" alt="智坊 DevForge" />
          <div className="login-brand-copy">
            <h1 className="login-hero-title">把项目交给 AI 团队</h1>
            <p className="login-hero-copy">需求、知识、设计、研发、交付，一站式进入。</p>
          </div>
        </div>
      </section>

      <section className="login-card-shell">
        <div className="login-card-glow" />
        <div className="login-card">
          <div className="login-card-topline">SECURE LOGIN</div>
          <h2><span className="login-title-orb" aria-hidden="true" />登录工作台</h2>
          <p>继续你的 AI 项目工作流</p>
          <Form form={form} onFinish={handleSubmit} layout="vertical" size="large" className="login-form">
            <Form.Item name="username" rules={[{ required: true, message: '请输入邮箱或用户名' }]}>
              <Input prefix={<UserOutlined />} placeholder="邮箱或用户名" autoComplete="username" />
            </Form.Item>
            <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
              <Input.Password prefix={<LockOutlined />} placeholder="请输入密码" autoComplete="current-password" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading} className="login-submit-btn">登 录</Button>
          </Form>
          <div className="login-register-row">
            <span>还没有账号？</span>
            <button type="button" onClick={() => setRegOpen(true)}>注册账号</button>
          </div>
        </div>
      </section>

      <Modal
        title={null}
        open={regOpen}
        onCancel={() => setRegOpen(false)}
        footer={null}
        width={520}
        destroyOnClose
        centered
        className="trial-account-modal"
      >
        <div className="trial-account-card">
          <div className="trial-account-aura" aria-hidden="true" />
          <div className="trial-account-orbit" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div className="trial-account-icon">
            <CloudServerOutlined />
          </div>
          <div className="trial-account-kicker">LOCAL DEPLOYMENT</div>
          <h3>本产品为本地部署</h3>
          <p>如需线上试用，请联系管理员申请试用账号。管理员会根据试用场景开通账号、权限与项目空间。</p>
          <div className="trial-account-steps">
            <div><SafetyCertificateOutlined /><span>数据留在部署环境</span></div>
            <div><TeamOutlined /><span>管理员统一分配账号</span></div>
            <div><ApartmentOutlined /><span>按项目开通试用空间</span></div>
          </div>
          <Button type="primary" size="large" className="trial-account-btn" onClick={() => setRegOpen(false)} block>
            我知道了
          </Button>
        </div>
      </Modal>
      <div className="login-footer">© 2026 智坊 DevForge · v0.4.5</div>
    </div>
  );
}
